/*
 *  -----------------------------------------------------------------------
 *    <copyright file="actions-email-dialog.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import 'date-fns';
import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogContent } from '@mui/material';
import DialogTitle from '@mui/material/DialogTitle';
import { union } from 'lodash';
import EmailActionList from '@components/central-monitoring/actions-email-dialog/email-action-list';
import Button from '@components/core/buttons/button';
import CharactersRemaining from '@components/core/characters-remaining';
import { CustomDialog } from '@components/core/dialog';
import { Accordion, AccordionSummary } from '@components/core/expansion-panel';
import TextField from '@components/core/text-field';
import MultiSelectEmail from '../../core/multi-select/multi-select-email';
import { useBoolean, useTranslation } from '@hooks/index';
import { SEND_CM_ACTION_EMAIL } from '@queries/central-monitoring';
import { StudyMemberFields, StudyFields, CompleteRiskFields } from '@queries/generated/fragments.generated';
import { riskObservations_OpraQueries_riskObservations_RiskObservation } from '@queries/generated/legacy-risk-assessment.generated';
import { SEND_RISK_MONITORING_EMAIL } from '@queries/legacy-risk-assessment';
import './actions-email-dialog.scss';

interface ActionsEmailDialogProps {
    open: boolean;
    actions: any;
    observations: riskObservations_OpraQueries_riskObservations_RiskObservation[] | any[];
    risks: CompleteRiskFields[];
    study: StudyFields;
    close(action?: string): any;
    query?: 'SEND_CM_ACTION_EMAIL';
    canEdit?: boolean;
}

interface ActionsDialogEmailState {
    additionalEmail: string;
    notes: string;
}

const ActionsEmailDialog = ({
    open,
    close,
    actions,
    observations,
    risks,
    study,
    query,
    canEdit,
}: ActionsEmailDialogProps) => {
    const { t } = useTranslation();
    const [sendEmail] = useMutation(
        query === 'SEND_CM_ACTION_EMAIL' ? SEND_CM_ACTION_EMAIL : SEND_RISK_MONITORING_EMAIL,
    );
    const disabledSendButton = useBoolean(false);
    const [errors, setErrors] = useState({} as any);
    const initState: ActionsDialogEmailState = {
        additionalEmail: '',
        notes: '',
    };
    const [state, setState] = useState<ActionsDialogEmailState>(initState);

    const ownerEmail = actions[0]?.owner?.user?.email ?? actions[0]?.owner?.email;

    const validation = () => {
        let isValid = true;
        let notesError = '';

        if (state.notes.length > 4000) {
            isValid = false;
            notesError = `${t('limitExceededBy')} ${state.notes.length - 4000} ${t('characters')}`;
        }

        setErrors({ ...errors, notes: notesError });

        return isValid;
    };

    useEffect(() => {
        validation();
    }, [state]);

    let actionsForEmail: any[] = [];

    if (!query)
        actionsForEmail = actions.map((action: any) => ({
            ...action,
            _observation: observations.find(
                (observation: riskObservations_OpraQueries_riskObservations_RiskObservation) =>
                    observation.id === action.observationId,
            ),
            _risk: risks?.find((risk: CompleteRiskFields) => risk?.id === action.riskId) ?? null,
        }));
    else
        actionsForEmail = actions.map((action: any) => ({
            ...action,
            _observation: observations.find(
                (observation: riskObservations_OpraQueries_riskObservations_RiskObservation) =>
                    observation.id === action.observationId,
            ),
            _risk: risks?.find((risk: CompleteRiskFields) => risk?.id === action.riskId) ?? null,
            _site: observations.find((observation: any) => observation.id === action.observationId)?._site,
        }));

    const onChange = (name: string) => (event: any) => {
        setState({
            ...state,
            [name]: event.target.value,
        });
    };

    const onChangeEmail = (additionalEmail: string) => {
        setState({
            ...state,
            additionalEmail,
        });
    };

    const onClose = (action: string) => {
        setState(initState);
        close(action);
    };

    const onSubmit = () => {
        disabledSendButton.setTrue();
        if (validation()) {
            sendEmail({
                variables: {
                    cc: state.additionalEmail,
                    notes: state.notes,
                    observationIds: union(actionsForEmail.map((action: any) => action._observation.id)),
                    actionIds: union(actionsForEmail.map((action: any) => action.id)),
                },
            })
                .then(() => {
                    disabledSendButton.setFalse();
                    onClose('done');
                })
                .catch(() => {
                    disabledSendButton.setFalse();
                    onClose('error');
                });
        }
    };

    const getCcMembers = () =>
        study.members.filter((member: StudyMemberFields) => member.active && member.email !== ownerEmail);

    return (
        <CustomDialog
            className="email-actions-modal"
            onClose={close}
            fullWidth
            maxWidth="md"
            scroll="paper"
            open={open}
        >
            <DialogTitle id="email-modal-title">
                {t('ra:emailObservationsActions')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => onClose('')}
                    data-testid="close-dialog"
                />
            </DialogTitle>

            <DialogContent style={{ paddingLeft: 20, paddingRight: 20 }}>
                <div className="actions-email-btn-wrapper">
                    <Button className="summary-save-btn cancel" text="cancel" onClick={() => onClose('')} />
                    <Button
                        className="summary-save-btn"
                        disabled={
                            disabledSendButton.value ||
                            Object.keys(errors).filter((item: any) => errors[item]).length > 0 ||
                            !canEdit
                        }
                        text="ra:sendEmail"
                        onClick={onSubmit}
                    />
                </div>

                <div className="email-form">
                    <div className="header">
                        {query
                            ? t('cm:cmActionAlert', { studyNumber: study?.studyNumber })
                            : t('ra:riskActionAlert', { studyNumber: study?.studyNumber })}
                    </div>
                    <Accordion
                        expanded={false}
                        className="email-expandable-panel"
                        slotProps={{ heading: { component: 'div' } }}
                    >
                        <AccordionSummary className="expand-summary">
                            <TextField
                                label={t('ra:to')}
                                id="action-email-owner"
                                className="action-email-owner"
                                rows={1}
                                fullWidth
                                value={ownerEmail}
                                placeholder={t('ra:criticalVariableDescriptionHelperText')}
                                variant="outlined"
                                color="secondary"
                                InputProps={{ readOnly: true, labelWidth: 20 }}
                            />
                            <MultiSelectEmail
                                people={getCcMembers()}
                                onChange={onChangeEmail}
                                label={t('ra:cc')}
                                customId="action-email-cc"
                                customClassName="action-email-cc"
                                placeholder={t('ra:additionalEmailAddresses')}
                            />
                            <TextField
                                label={t('ra:notes')}
                                id="action-email-description"
                                className="action-email-description"
                                multiline
                                rows={5}
                                fullWidth
                                value={state.notes}
                                onChange={onChange('notes')}
                                placeholder={t('ra:enterNotes')}
                                variant="outlined"
                                color="secondary"
                                InputProps={{ labelWidth: 45 }}
                                helperText={
                                    <CharactersRemaining
                                        id="actions-email-dialog-notes"
                                        length={state.notes.length}
                                        max={4000}
                                        error={errors.notes}
                                    />
                                }
                                error={!!errors.notes}
                            />
                        </AccordionSummary>
                    </Accordion>
                </div>
                <EmailActionList actions={actionsForEmail} isCM={Boolean(query)} />
            </DialogContent>
        </CustomDialog>
    );
};

export default ActionsEmailDialog;
